import * as React from 'react';
import { useTranslation } from 'next-i18next';
import tw, { styled } from 'twin.macro';
import Image from 'next/image';
import { BaseModal, ButtonV2 } from '@restworld/ui-ds';
import { getAssetSrc } from '@restworld/utils-common';

interface PaymentMethod {
  id: string;
  customer: string;
  brand?: string;
  last4?: string;
  expiry_month?: number;
  expiry_year?: number;
}

interface DetachPaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  paymentMethod: PaymentMethod | null;
  isDetaching?: boolean;
}

// Payment provider logo mapping
const paymentProviderLogoMap: Record<string, string> = {
  mastercard: getAssetSrc('/utils-assets/mastercard.svg'),
  visa: getAssetSrc('/utils-assets/visa.svg'),
  maestro: getAssetSrc('/utils-assets/maestro.svg'),
  amex: getAssetSrc('/utils-assets/amex.svg'),
};

const ModalContent = styled.div`
  ${tw`flex flex-col gap-10 items-center w-full`}
`;

const TextContent = styled.div`
  ${tw`flex flex-col gap-2.5 items-start text-center w-full`}
`;

const Title = styled.h2`
  ${tw`text-[20px] font-semibold text-[#1c1c33] w-full leading-[1.5]`}
`;

const Description = styled.p`
  ${tw`text-[16px] font-normal text-[#1c1c33] w-full leading-[1.5]`}
`;

const PaymentMethodSection = styled.div`
  ${tw`bg-[#fafafa] border border-[#dedee1] rounded-lg p-3 flex items-center justify-between w-full`}
`;

const PaymentMethodInfo = styled.div`
  ${tw`flex gap-8 items-center flex-1`}
`;

const PaymentMethodDetails = styled.div`
  ${tw`flex flex-col gap-1 items-start justify-center`}
`;

const CardBrandText = styled.p`
  ${tw`text-[14px] font-medium text-[#1c1c33] leading-normal`}
`;

const ExpiryText = styled.p`
  ${tw`text-[14px] font-medium text-[#1c1c33] leading-normal`}
`;

const ButtonsContainer = styled.div`
  ${tw`flex flex-col gap-2.5 w-full`}
`;

export const DetachPaymentMethodModal: React.FC<DetachPaymentMethodModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  paymentMethod,
  isDetaching = false,
}) => {
  const { t } = useTranslation('jp-onboarding');

  if (!paymentMethod) return null;

  const paymentBrand = paymentMethod.brand?.toLowerCase();
  const paymentLogoSrc = paymentBrand && paymentProviderLogoMap[paymentBrand];

  const footer = (
    <ButtonsContainer>
      <ButtonV2 variant="secondary-danger" onClick={onConfirm} disabled={isDetaching} loading={isDetaching} tw="h-12">
        {t('steps.show_payment_methods.delete_payment_method')}
      </ButtonV2>
      <ButtonV2 variant="tertiary" onClick={onClose} disabled={isDetaching} tw="h-12">
        {t('steps.show_payment_methods.cancel')}
      </ButtonV2>
    </ButtonsContainer>
  );

  return (
    <BaseModal open={isOpen} onClose={onClose} footer={footer} showCloseIcon>
      <ModalContent>
        <TextContent>
          <Title>{t('steps.show_payment_methods.delete_modal_title')}</Title>
          <Description>{t('steps.show_payment_methods.delete_modal_description')}</Description>
        </TextContent>

        <PaymentMethodSection>
          <PaymentMethodInfo>
            <PaymentMethodDetails>
              {paymentLogoSrc && (
                <Image src={paymentLogoSrc} alt={paymentBrand || ''} width={23} height={16} tw="object-contain" />
              )}
              <CardBrandText>
                {paymentMethod.brand} ***{paymentMethod.last4}
              </CardBrandText>
            </PaymentMethodDetails>
            <ExpiryText>
              {t('steps.show_payment_methods.expiration')}:{' '}
              {`${String(paymentMethod.expiry_month).padStart(2, '0')}/${String(paymentMethod.expiry_year).slice(-2)}`}
            </ExpiryText>
          </PaymentMethodInfo>
        </PaymentMethodSection>
      </ModalContent>
    </BaseModal>
  );
};

export default DetachPaymentMethodModal;
